import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import {
  colorAccent,
  colorRed,
  colorWhite,
} from '@/utils/palette';
import {
  left,
  marginLeft,
  marginRight,
} from '@/utils/directions';
import { preloadRoute } from '@/containers/App/routeLoaders';

const SidebarLink = ({
 title, icon, newLink, route, onClick, chevron,
}) => {
  const content = (
    <>
      {icon ? <SidebarLinkIcon className={`lnr lnr-${icon}`} /> : ''}
      <SidebarLinkTitle>
        {title}
        {newLink ? (
          <NewBadge bg="custom">
            <span>New</span>
          </NewBadge>
        ) : ''}
      </SidebarLinkTitle>
      {chevron ? (
        <SidebarLinkChevron className="lnr lnr-chevron-right" />
      ) : ''}
    </>
  );

  return (
    <li>
      {route ? (
        <SidebarNavLink
          to={route}
          onClick={onClick}
          onMouseEnter={() => preloadRoute(route)}
          onFocus={() => preloadRoute(route)}
          activeClassName="active"
        >
          {content}
        </SidebarNavLink>
      ) : (
        <SidebarNavLink
          as="button"
          type="button"
          onClick={onClick}
        >
          {content}
        </SidebarNavLink>
      )}
    </li>
  );
};

SidebarLink.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  newLink: PropTypes.bool,
  route: PropTypes.string,
  onClick: PropTypes.func,
  chevron: PropTypes.bool,
};

SidebarLink.defaultProps = {
  icon: '',
  newLink: false,
  route: '/',
  onClick: () => {},
  chevron: false,
};

export default SidebarLink;

// region STYLES

export const SidebarNavLink = styled(NavLink)`
  min-height: 46px;
  width: 266px;
  transition: all 0.3s;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025);
  border: none;
  border-radius: 14px;
  color: rgba(255, 255, 255, 0.88);
  text-align: ${left};
  font-size: 14px;
  backdrop-filter: blur(6px);

  &.active {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.05));
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);

    &:before {
      opacity: 1;
    }
  }

  &:before {
    content: "";
    position: absolute;
    top: 9px;
    height: calc(100% - 18px);
    width: 3px;
    border-radius: 999px;
    background: linear-gradient(180deg, ${colorAccent}, #ffba78);
    opacity: 0;
    transition: all 0.3s;
    ${left}: 7px;
  }

  &:hover {
    text-decoration: none;
    background-color: rgba(255, 255, 255, 0.075);
    color: ${colorWhite};
    transform: translateX(1px);

    &:before {
      opacity: 1;
    }
  }

  @media screen and (min-width: 576px) {
    width: 100%;

    span {
      position: relative;
      animation: none;
      ${left}: 0;
    }
  }
`;

const NewBadge = styled(Badge)`
  width: 26px;
  height: 14px;
  background-color: ${colorRed};
  font-size: 8px;
  font-weight: 400;
  padding: 2px;
  line-height: 9px;
  position: relative;
  text-transform: uppercase;
  border-radius: 7px;
  ${marginLeft}: 5px;

  span {
    position: absolute;
    top: 3px;
    width: 26px;
    text-align: center;
    ${left}: 0;
  }
`;

export const SidebarLinkTitle = styled.span`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

export const SidebarLinkChevron = styled.span`
  margin-left: auto;
  font-size: 10px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.72);
`;

export const SidebarLinkIcon = styled.span`
  min-width: 30px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 14px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
  ${marginRight}: 0;
`;

// endregion
